<script setup lang="ts">
import { useRouter } from 'vue-router'
import { Moon, Sunny, SwitchButton } from '@element-plus/icons-vue'

import { useAuthStore } from '@/stores/auth'
import { useTheme } from '@/composables/useTheme'

const auth = useAuthStore()
const router = useRouter()
const { isDark, toggle } = useTheme()

function logout(): void {
  auth.logout()
  router.replace({ name: 'login' })
}
</script>

<template>
  <header class="app-header">
    <router-link to="/library" class="brand">
      <span class="logo" aria-hidden="true"></span>
      <b>GUESS365</b>
      <span class="tagline">Dataset Version Control</span>
    </router-link>

    <nav>
      <router-link to="/library" class="link">照片庫</router-link>
      <router-link to="/upload" class="link">上傳</router-link>
      <router-link to="/datasets" class="link">訓練資料集</router-link>
      <router-link to="/leaderboard" class="link">排行榜</router-link>
      <router-link v-if="auth.isSuperadmin" to="/admin" class="link">管理</router-link>
    </nav>

    <div class="right">
      <button class="icon-btn" :title="isDark ? '切換淺色' : '切換深色'" @click="toggle">
        <el-icon :size="17"><Moon v-if="!isDark" /><Sunny v-else /></el-icon>
      </button>
      <div v-if="auth.user" class="user">
        <span class="avatar">{{ (auth.user.full_name || auth.user.email).slice(0, 1).toUpperCase() }}</span>
        <div class="who">
          <span class="name">{{ auth.user.full_name || auth.user.email }}</span>
          <span class="role">{{ auth.user.role }}</span>
        </div>
      </div>
      <button class="icon-btn" title="登出" @click="logout">
        <el-icon :size="17"><SwitchButton /></el-icon>
      </button>
    </div>
  </header>
</template>

<style scoped>
.app-header {
  display: flex;
  align-items: center;
  gap: 24px;
  height: 56px;
  padding: 0 24px;
  background: var(--surface);
  border-bottom: 1px solid var(--line);
  position: sticky;
  top: 0;
  z-index: 20;
}
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: var(--ink);
  flex-shrink: 0;
}
.logo {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--accent);
  position: relative;
}
.logo::after {
  content: '';
  position: absolute;
  inset: 7px;
  border: 2px solid var(--surface);
  border-radius: 3px;
}
.brand b { font-weight: 700; letter-spacing: .3px; font-size: 15px; }
.tagline {
  color: var(--faint);
  font-family: var(--font-mono);
  font-size: 11.5px;
  letter-spacing: .04em;
  border-left: 1px solid var(--line-strong);
  padding-left: 10px;
}
nav { display: flex; gap: 2px; flex: 1; }
.link {
  color: var(--muted);
  text-decoration: none;
  font-size: 14px;
  padding: 7px 13px;
  border-radius: var(--radius);
  border-bottom: 2px solid transparent;
}
.link:hover { background: var(--raise); color: var(--ink); }
.link.router-link-active {
  color: var(--accent-ink);
  font-weight: 600;
  background: var(--accent-bg);
  border-bottom-color: var(--accent);
  border-radius: var(--radius) var(--radius) 0 0;
}
.right { display: flex; align-items: center; gap: 10px; }
.icon-btn {
  width: 34px;
  height: 34px;
  border-radius: var(--radius);
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--muted);
  cursor: pointer;
  display: grid;
  place-items: center;
}
.icon-btn:hover { background: var(--raise); color: var(--ink); }
.user { display: flex; align-items: center; gap: 9px; }
.avatar {
  width: 30px;
  height: 30px;
  border-radius: var(--radius);
  background: var(--accent-bg);
  border: 1px solid var(--line);
  color: var(--accent-ink);
  display: grid;
  place-items: center;
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}
.who { display: flex; flex-direction: column; line-height: 1.25; }
.name { font-size: 13.5px; color: var(--ink); }
.role {
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--accent-ink);
}
@media (max-width: 720px) {
  .tagline, .who { display: none; }
  .app-header { gap: 14px; padding: 0 14px; }
}

/* 手機版：導覽列橫向捲動、不換行、不爆版 */
@media (max-width: 760px) {
  .app-header { gap: 8px; padding: 0 10px; }
  nav {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;        /* Firefox */
  }
  nav::-webkit-scrollbar { display: none; }
  .link {
    white-space: nowrap;
    flex: none;
    font-size: 13.5px;
    padding: 6px 10px;
  }
  .brand b { font-size: 14px; }
  .right { gap: 6px; }
}
</style>
